// Live bell over the notification service's WebSocket.
//
// This is an ACCELERATOR, never the source of truth. The push carries enough to
// re-render immediately, but the authoritative feed is still the REST query
// these hooks already own — so when the socket is off, misconfigured, or simply
// down, the bell behaves exactly as it did before this file existed.

import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  createBellSocket,
  type BellPush,
} from "@playbookai/notification-client/browser";
import { publishEntityAuditChanged } from "@playbookai/api-clients";
import { useAuthStore } from "../auth/auth-store.js";
import { mintWsTicket } from "../auth/session-api.js";
import { getRuntimeConfig } from "../config/runtime.js";
import {
  notificationInboxQueryPrefix,
  notificationsQueryKey,
  unreadCountQueryKey,
} from "./useNotifications.js";

/**
 * Asks the BFF for a single-use WebSocket ticket.
 *
 * A browser cannot set an Authorization header on a WebSocket handshake, and a
 * token in the query string ends up in every proxy access log along the path.
 * A short-lived, single-use ticket is the standing pattern here — collab's
 * socket authenticates the same way.
 *
 * Goes through `mintWsTicket` rather than a second hand-rolled `fetch`.
 * `POST /api/auth/ws-ticket` is an unsafe method behind the BFF's `CsrfGuard`,
 * and `mintWsTicket` is the one place that attaches `X-CSRF-Token`. Calling
 * `fetch` directly here skipped that header, so the guard answered 403 on every
 * mint and the socket could never complete a handshake.
 */
async function mintTicket(): Promise<string | null> {
  try {
    const { ticket } = await mintWsTicket();
    return ticket;
  } catch {
    // The socket is an accelerator, so a failed mint degrades to REST rather
    // than surfacing an error — same contract as before.
    return null;
  }
}

/**
 * Opens the bell socket for the signed-in user and folds each push into the
 * existing feed cache.
 *
 * Mount this ONCE, high in the tree. Two mounts would open two sockets and
 * double every push.
 */
export function useBellSocket(): void {
  const queryClient = useQueryClient();
  const tenantId = useAuthStore((s) => s.claims?.tenantId);
  const userId = useAuthStore((s) => s.claims?.userId);

  useEffect(() => {
    const url = getRuntimeConfig().api.notificationSocketUrl;
    // No scope yet (auth still hydrating) or no socket configured: do nothing.
    // REST already covers both cases.
    if (!url || !tenantId || !userId) return;

    const key = notificationsQueryKey(tenantId, userId);
    const inboxPrefix = notificationInboxQueryPrefix(tenantId, userId);
    const countKey = unreadCountQueryKey(tenantId, userId);

    const socket = createBellSocket({
      url,
      mintTicket,
      onPush: (push: BellPush) => {
        // Invalidate rather than splicing the pushed item into the list.
        //
        // The push says WHAT changed but not where it belongs: the feed is
        // ordered on the server by "last fired" and coalescing can move an
        // existing row rather than adding one, so a client-side insert would
        // guess at both position and whether the row is new. Invalidating keeps
        // the server the single authority on ordering, at the cost of one
        // request — and that request only happens when something actually
        // arrived, which is the win over polling.
        void queryClient.invalidateQueries({ queryKey: key });
        void queryClient.invalidateQueries({ queryKey: inboxPrefix });
        // The COUNT is the one case where that reasoning does not apply, so it
        // is written straight in. The argument above is about a list's ordering;
        // a scalar has no ordering to get wrong, and the server computed this
        // number for this recipient in the same query that produced the push.
        // BellPush's own contract says so — "enough to render badge and toast
        // with NO refetch, which is the whole point of pushing" — and until now
        // the shell discarded it and refetched anyway.
        //
        // CANCEL FIRST. A poll tick can have a count request in flight when the
        // push lands; its response resolves afterwards and would overwrite the
        // pushed value with a number read BEFORE this notification existed.
        // Cancelling is the documented remedy and is safe here because the push
        // is strictly fresher than anything already on the wire.
        void queryClient
          .cancelQueries({ queryKey: countKey })
          .finally(() =>
            queryClient.setQueryData<number>(countKey, push.unreadCount),
          );
      },
      /* The audit nudge rides the same socket, and the shell's whole job is to
         hand it to the bus. It deliberately learns NOTHING about audit query
         keys: the four History rails live in feature packages that may not
         import the shell, so they subscribe to the bus instead and each
         invalidates its own feed. The bus owns the throttle. */
      onAuditChanged: () => publishEntityAuditChanged(),
    });

    socket.start();
    // Closing on unmount matters more here than usual: the socket holds a
    // subscription on the server, and a tenant or session switch must not keep
    // delivering the previous scope's notifications.
    return () => socket.stop();
  }, [queryClient, tenantId, userId]);
}
